import * as React from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import { MULTI_SELECT_ITEM_HEIGHT } from './helpers';
import { Option } from './types';

const LISTBOX_PADDING = 8;

interface RowData {
  items: {
    isExpanded: boolean;
    item: Option;
    level: number;
    render: (item: Option) => React.ReactNode;
  }[];
}

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const { items } = data as RowData;
  const { item, level, render } = items[index];

  const inlineStyle = {
    ...style,
    left: level * 16,
    paddingLeft: LISTBOX_PADDING,
    top: (style.top as number) + LISTBOX_PADDING,
    width: `calc(100% - ${level * 16}px)`,
  };
  const key = item.label + item.value;

  return (
    <div style={inlineStyle} key={key}>
      {render(item)}
    </div>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = 'OuterElementType';

function useResetCache(data: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

interface VirtualizedListboxProps {
  expandedValues: Set<string>;
  maxHeight: number;
  options: Option[];
  renderOption: (item: Option) => React.ReactNode;
}

const VirtualizedListbox = React.forwardRef<
  HTMLDivElement,
  VirtualizedListboxProps
>(function VirtualizedListbox(props, ref) {
  const { expandedValues, maxHeight, options, renderOption } = props;

  // Updated flattening logic to handle nested items better
  const itemData = React.useMemo(() => {
    const flattenedItems: RowData['items'] = [];
    const seen = new Set<string>();

    const flatten = (items: Option[], level = 0) => {
      items.forEach(item => {
        // Prevent duplicate items
        if (seen.has(item.value)) return;
        seen.add(item.value);

        flattenedItems.push({
          isExpanded: expandedValues.has(item.value),
          item,
          level,
          render: renderOption,
        });

        if (item.options && expandedValues.has(item.value)) {
          flatten(item.options, level + 1);
        }
      });
    };

    flatten(options);
    return { items: flattenedItems };
  }, [options, expandedValues, renderOption]);

  // Calculate visible height
  const visibleHeight = React.useMemo(() => {
    const totalItems = itemData.items.length;
    const heightWithPadding =
      totalItems * MULTI_SELECT_ITEM_HEIGHT + 2 * LISTBOX_PADDING;
    return Math.min(maxHeight, heightWithPadding);
  }, [itemData.items.length, maxHeight]);

  const gridRef = useResetCache(itemData.items.length);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={{}}>
        <VariableSizeList
          itemCount={itemData.items.length}
          itemData={itemData}
          itemSize={() => MULTI_SELECT_ITEM_HEIGHT + 4} // Add small buffer for margins
          height={visibleHeight}
          width='100%'
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType='div'
          overscanCount={5}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

VirtualizedListbox.displayName = 'VirtualizedListbox';

export default VirtualizedListbox;
